import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { Add as AddIcon, MoreVert } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "components/Toast";
import { createArea, createState, getStates } from "services/vehicle";
import DashboardLayout from "layout";

// First, update the state management
const States = () => {
  const [open, setOpen] = useState(false);
  const [editingState, setEditingState] = useState<any>(null);
  const [stateName, setStateName] = useState("");
  const [airportName, setAirportName] = useState(""); // Add this line

  // Update the handleClose function
  const handleClose = () => {
    setOpen(false);
    setEditingState(null);
    setStateName("");
    setAirportName(""); // Add this line
  };

  // Update the handleSubmit function
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      name: stateName,
      airport: airportName, // Add airport name to mutation
    });
  };
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedState, setSelectedState] = useState<any>(null);
  const [openAreaDialog, setOpenAreaDialog] = useState(false);
  const [areaName, setAreaName] = useState("");

  const createAreaMutation = useMutation({
    mutationFn: createArea,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["states"] });
      showToast("Area added successfully!", "success");
      handleCloseAreaDialog();
    },
    onError: () => {
      showToast("Failed to add area", "error");
    },
  });

  const { data: states } = useQuery({
    queryKey: ["states"],
    queryFn: getStates,
  });

  const createMutation = useMutation({
    mutationFn: createState,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["states"] });
      showToast("State added successfully!", "success");
      handleClose();
    },
    onError: () => {
      showToast("Failed to add state", "error");
    },
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, state: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedState(state);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenAreaDialog = () => {
    handleMenuClose();
    setOpenAreaDialog(true);
  };

  const handleCloseAreaDialog = () => {
    setOpenAreaDialog(false);
    setAreaName("");
  };

  const handleAreaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedState) {
      createAreaMutation.mutate({
        name: areaName,
        state: selectedState._id,
      });
    }
  };

  const handleOpen = (state?: any) => {
    if (state) {
      setEditingState(state);
      setStateName(state.name);
    } else {
      setEditingState(null);
      setStateName("");
    }
    setOpen(true);
  };

  // Update the Dialog form
  return (
    <DashboardLayout nextSideBar>
      <Box sx={{ p: { xs: 0, sm: 3 }, pb: { xs: 3, sm: 3 } }}>
        <Typography
          color="#101828"
          fontSize={{ xs: "20px", sm: "24px" }}
          fontWeight={600}
          pb={{ xs: 2, sm: 3 }}
        >
          Manage States and Areas
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              bgcolor: "#379E66",
              "&:hover": { bgcolor: "#2b7d4f" },
            }}
          >
            Add State
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#FFF" }}>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Name</TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    py: 1.5,
                    width: "80px",
                    textAlign: "right",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {states?.data?.map((state: any) => (
                <TableRow key={state._id}>
                  <TableCell sx={{ py: 1 }}>{state.name}</TableCell>
                  <TableCell sx={{ py: 1, textAlign: "right" }}>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, state)}
                      size="small"
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>{editingState ? "Edit State" : "Add State"}</DialogTitle>
          <DialogContent>
            <form onSubmit={handleSubmit}>
              <TextField
                autoFocus
                margin="dense"
                label="State Name"
                fullWidth
                variant="outlined"
                value={stateName}
                onChange={(e) => setStateName(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Airport Name"
                fullWidth
                variant="outlined"
                value={airportName}
                onChange={(e) => setAirportName(e.target.value)}
                sx={{ mt: 2 }}
              />
              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={handleClose} sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ bgcolor: "#379E66", "&:hover": { bgcolor: "#2b7d4f" } }}
                  disabled={createMutation.isPending}
                >
                  {editingState ? "Update" : "Add"}
                </Button>
              </Box>
            </form>
          </DialogContent>
        </Dialog>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
              mt: 1,
            },
          }}
        >
          <MenuItem
            onClick={handleOpenAreaDialog}
            sx={{
              fontSize: "14px",
              py: 1,
              px: 2,
            }}
          >
            Add Area
          </MenuItem>
        </Menu>

        <Dialog
          open={openAreaDialog}
          onClose={handleCloseAreaDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Area to {selectedState?.name}</DialogTitle>
          <DialogContent>
            <form onSubmit={handleAreaSubmit}>
              <TextField
                autoFocus
                margin="dense"
                label="Area Name"
                fullWidth
                variant="outlined"
                value={areaName}
                onChange={(e) => setAreaName(e.target.value)}
              />
              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={handleCloseAreaDialog} sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ bgcolor: "#379E66", "&:hover": { bgcolor: "#2b7d4f" } }}
                  disabled={createAreaMutation.isPending}
                >
                  Add
                </Button>
              </Box>
            </form>
          </DialogContent>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
};

export default States;
